// Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
// See LICENSE in the project root for license information.
export function computeFilterSetsCore(options) {
    const { operations, executionStates, currentFilter, searchQuery, computeDisplayStatus } = options;
    const filteredOutNames = new Set();
    const searchFilteredOutNames = new Set();
    const visibleOperations = [];
    const query = searchQuery.trim().toLowerCase();
    for (const op of operations.values()) {
        const state = executionStates.get(op.name) || undefined;
        // Merge dynamic fields so rendering logic can treat operation rows uniformly.
        op.runInThisIteration = state === null || state === void 0 ? void 0 : state.runInThisIteration;
        op.status = (state === null || state === void 0 ? void 0 : state.status) || op.status;
        op.isActive = state === null || state === void 0 ? void 0 : state.isActive;
        op.logFileURLs = state === null || state === void 0 ? void 0 : state.logFileURLs;
        const effectiveStatus = computeDisplayStatus(op);
        if (currentFilter === 'failed-warn') {
            const includeInFailedWarn = effectiveStatus === 'Failure' || effectiveStatus === 'SuccessWithWarning';
            if (!includeInFailedWarn) {
                filteredOutNames.add(op.name);
                continue;
            }
        }
        if (query && !op.name.toLowerCase().includes(query)) {
            searchFilteredOutNames.add(op.name);
            continue;
        }
        visibleOperations.push(op);
    }
    return {
        visibleOperations,
        filteredOutNames,
        searchFilteredOutNames
    };
}
export function pruneGraphOperations(baseOperations) {
    if (!baseOperations.length)
        return baseOperations;
    const byName = new Map();
    baseOperations.forEach((op) => byName.set(op.name, op));
    const dependents = new Map();
    baseOperations.forEach((op) => {
        (op.dependencies || []).forEach((dependencyName) => {
            if (!byName.has(dependencyName))
                return;
            let setForDependency = dependents.get(dependencyName);
            if (!setForDependency) {
                setForDependency = new Set();
                dependents.set(dependencyName, setForDependency);
            }
            setForDependency.add(op.name);
        });
    });
    const active = new Set(baseOperations.map((op) => op.name));
    const noopSet = new Set(baseOperations.filter((op) => op.noop).map((op) => op.name));
    const incomingCount = new Map();
    const outgoingCount = new Map();
    baseOperations.forEach((op) => {
        incomingCount.set(op.name, (dependents.get(op.name) || new Set()).size);
        const outgoing = (op.dependencies || []).filter((dependencyName) => byName.has(dependencyName)).length;
        outgoingCount.set(op.name, outgoing);
    });
    const queue = [];
    active.forEach((nodeName) => {
        if (!noopSet.has(nodeName))
            return;
        const incoming = incomingCount.get(nodeName) || 0;
        const outgoing = outgoingCount.get(nodeName) || 0;
        if (incoming === 0 || incoming === 1 || outgoing === 1)
            queue.push(nodeName);
    });
    while (queue.length) {
        const nodeName = queue.pop();
        if (!nodeName || !active.has(nodeName))
            continue;
        active.delete(nodeName);
        const op = byName.get(nodeName);
        if (!op)
            continue;
        for (const dependencyName of op.dependencies || []) {
            if (!active.has(dependencyName))
                continue;
            const previous = incomingCount.get(dependencyName) || 0;
            incomingCount.set(dependencyName, Math.max(0, previous - 1));
            const incoming = incomingCount.get(dependencyName) || 0;
            const outgoing = outgoingCount.get(dependencyName) || 0;
            if (noopSet.has(dependencyName) && (incoming === 0 || incoming === 1 || outgoing === 1)) {
                queue.push(dependencyName);
            }
        }
        const dependentNodes = dependents.get(nodeName) || new Set();
        for (const dependentName of dependentNodes) {
            if (!active.has(dependentName))
                continue;
            const previousOutgoing = outgoingCount.get(dependentName) || 0;
            outgoingCount.set(dependentName, Math.max(0, previousOutgoing - 1));
            const incoming = incomingCount.get(dependentName) || 0;
            const outgoing = outgoingCount.get(dependentName) || 0;
            if (noopSet.has(dependentName) && (incoming === 0 || incoming === 1 || outgoing === 1)) {
                queue.push(dependentName);
            }
        }
    }
    const resolvedMemo = new Map();
    function resolveDeps(nodeName, seen) {
        const cached = resolvedMemo.get(nodeName);
        if (cached)
            return cached;
        if (seen.has(nodeName))
            return new Set();
        seen.add(nodeName);
        const op = byName.get(nodeName);
        const resolved = new Set();
        if (!op)
            return resolved;
        for (const dependencyName of op.dependencies || []) {
            if (!byName.has(dependencyName))
                continue;
            if (active.has(dependencyName)) {
                resolved.add(dependencyName);
            }
            else {
                const subResolved = resolveDeps(dependencyName, seen);
                for (const item of subResolved)
                    resolved.add(item);
            }
        }
        seen.delete(nodeName);
        resolvedMemo.set(nodeName, resolved);
        return resolved;
    }
    return baseOperations
        .filter((op) => active.has(op.name))
        .map((op) => {
        const dependencies = new Set();
        for (const dependencyName of op.dependencies || []) {
            if (!byName.has(dependencyName))
                continue;
            if (active.has(dependencyName)) {
                dependencies.add(dependencyName);
            }
            else {
                const subResolved = resolveDeps(dependencyName, new Set());
                for (const item of subResolved)
                    dependencies.add(item);
            }
        }
        return Object.assign({}, op, { dependencies: Array.from(dependencies) });
    });
}
//# sourceMappingURL=graphFiltering.js.map